import React, { useState, useEffect } from 'react'
import { submitToEmailAgent, getEmailAgentStatus } from '../utils/emailAgent'
import { SubmissionStatus, SubmissionResult } from '../types/webhook'

interface FormErrors {
  name?: string;
  email?: string;
  company?: string;
  linkedin?: string;
  projectType?: string;
  budget?: string;
  timeline?: string;
  message?: string;
}

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    linkedin: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: ''
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>('idle')
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult>({ status: 'idle' })
  const [emailAgentOnline, setEmailAgentOnline] = useState<boolean>(false)

  // Check Email Agent status on component mount
  useEffect(() => {
    const checkEmailAgent = async () => {
      try {
        const status = await getEmailAgentStatus()
        setEmailAgentOnline(status.available)
        console.log('📧 Email Agent Status:', status)
      } catch (error) {
        console.warn('❌ Could not check Email Agent status:', error)
        setEmailAgentOnline(false)
      }
    }

    checkEmailAgent()
  }, [])

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateLinkedIn = (url: string): boolean => {
    if (!url) return true // LinkedIn is optional
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/
    return linkedinRegex.test(url)
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.name.trim())) {
      newErrors.name = 'Name can only contain letters, spaces, hyphens, and apostrophes'
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required'
    } else if (!validateEmail(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address'
    }

    // LinkedIn validation (optional but must be valid if provided)
    if (formData.linkedin.trim() && !validateLinkedIn(formData.linkedin.trim())) {
      newErrors.linkedin = 'Please enter a valid LinkedIn profile URL (e.g., https://linkedin.com/in/your-name)'
    }

    // Project type validation
    if (!formData.projectType) {
      newErrors.projectType = 'Please select a project type'
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Project description is required'
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Please provide more details about your project (minimum 20 characters)'
    } else if (formData.message.trim().length > 1000) {
      newErrors.message = 'Project description is too long (maximum 1000 characters)'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setSubmissionStatus('submitting')
    setSubmissionResult({ status: 'submitting' })
    
    try {
      console.log('🚀 Submitting to Email Agent...')
      
      // Submit to N8N Email Agent
      const response = await submitToEmailAgent({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        linkedin: formData.linkedin,
        projectType: formData.projectType,
        budget: formData.budget,
        timeline: formData.timeline,
        message: formData.message
      })
      
      console.log('✅ Email Agent Response:', response)
      
      setSubmissionStatus('success')
      setSubmissionResult({
        status: 'success',
        message: response.message || 'Your message has been sent! Our AI agent will respond shortly.'
      })
      
      // Reset form on success
      setFormData({
        name: '',
        email: '',
        company: '',
        linkedin: '',
        projectType: '',
        budget: '',
        timeline: '',
        message: ''
      })
      setErrors({})
      
    } catch (error) {
      console.error('❌ Email Agent Error:', error)
      
      setSubmissionStatus('error')
      setSubmissionResult({
        status: 'error',
        error: error instanceof Error ? error.message : 'Failed to send message. Please try again.'
      })
    }
  }

  const contactInfo = [
    {
      icon: (
        <div className="w-10 h-10 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="white" strokeWidth="2" fill="none"/>
            <polyline points="22,6 12,13 2,6" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      ),
      title: "Email",
      value: "info@oneaiconsulting.com",
      href: "mailto:info@oneaiconsulting.com"
    },
    {
      icon: (
        <div className="w-10 h-10 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" stroke="white" strokeWidth="2" fill="white"/>
            <rect x="2" y="9" width="4" height="12" stroke="white" strokeWidth="2" fill="white"/>
            <circle cx="4" cy="4" r="2" stroke="white" strokeWidth="2" fill="white"/>
          </svg>
        </div>
      ),
      title: "LinkedIn",
      value: "Connect with us",
      href: "https://linkedin.com/company/oneai-consulting"
    },
    {
      icon: (
        <div className="w-10 h-10 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="white" strokeWidth="2" fill="none"/>
            <circle cx="12" cy="10" r="3" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      ),
      title: "Office",
      value: "San Jose, CA",
      href: "#"
    }
  ]

  const projectTypes = [
    "AI Agent Development",
    "Process Automation",
    "Data Intelligence",
    "Integration Services",
    "Consulting & Strategy",
    "Other"
  ]

  const budgetRanges = [
    "Under $10K",
    "$10K - $25K",
    "$25K - $50K",
    "$50K - $100K",
    "$100K+",
    "Let's discuss"
  ]

  const timelines = [
    "ASAP",
    "1-3 months",
    "3-6 months",
    "6-12 months",
    "12+ months",
    "Flexible"
  ]

  return (
    <div className="contact-page">
      {/* Email Agent Status Indicator */}
      {emailAgentOnline && (
        <div className="fixed top-4 right-4 z-40 bg-green-100 border border-green-200 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-green-800">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Email Agent Online
          </div>
        </div>
      )}

      {/* Success Modal */}
      {submissionResult.status === 'success' && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSubmissionResult({ status: 'idle' });
            }
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-fade-in">
            <button
              onClick={() => setSubmissionResult({ status: 'idle' })}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 select-text">Email Agent Activated!</h3>
              
              <div className="text-gray-600 mb-6 space-y-2 select-text">
                <p>{submissionResult.message}</p>
                <p className="text-sm">Our AI agent is already processing your request and will respond shortly.</p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 select-text">
                <h4 className="font-semibold text-blue-900 mb-2">What's happening now?</h4>
                <ul className="text-sm text-blue-700 space-y-1 text-left">
                  <li>• AI agent is analyzing your project requirements</li>
                  <li>• Generating a personalized response based on your needs</li>
                  <li>• You'll receive an intelligent reply within minutes</li>
                  <li>• A human expert will follow up for detailed discussions</li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setSubmissionResult({ status: 'idle' })}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Continue Browsing
                </button>
                <a
                  href="/blog"
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-center"
                >
                  Read Our Blog
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-8">
              Get in Touch
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6">
              Let's Build{' '}
              <span className="block">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Something Amazing
                </span>
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              Ready to transform your business with AI? Let's discuss your project 
              and explore how we can help you achieve your goals.
            </p>
            
            <a 
              href="#contact-form" 
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span className="mr-3">Start Your Project</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Get in{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Touch
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple ways to reach us. Choose what works best for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-16">
            {contactInfo.map((info, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">{info.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{info.title}</h3>
                {info.href && info.href !== '#' ? (
                  <a href={info.href} className="text-blue-600 hover:text-blue-700 transition-colors">
                    {info.value}
                  </a>
                ) : (
                  <p className="text-gray-600">{info.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Tell us about{' '}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  your project
                </span>
              </h2>
              <p className="text-xl text-gray-600">
                The more details you provide, the better we can understand your needs and provide an accurate proposal.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${
                      errors.name 
                        ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-4 focus:ring-red-100' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                    }`}
                    required
                    disabled={submissionStatus === 'submitting'}
                  />
                  {errors.name && (
                    <div className="mt-2">
                      <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg shadow-lg">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium">{errors.name}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${
                      errors.email 
                        ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-4 focus:ring-red-100' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                    }`}
                    required
                    disabled={submissionStatus === 'submitting'}
                  />
                  {errors.email && (
                    <div className="mt-2">
                      <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg shadow-lg">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium">{errors.email}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${
                      errors.company 
                        ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-4 focus:ring-red-100' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                    }`}
                    disabled={submissionStatus === 'submitting'}
                  />
                  {errors.company && (
                    <div className="mt-2">
                      <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg shadow-lg">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium">{errors.company}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="linkedin" className="block text-sm font-semibold text-gray-900 mb-2">
                    LinkedIn Profile
                  </label>
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/your-profile"
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${
                      errors.linkedin 
                        ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-4 focus:ring-red-100' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                    }`}
                    disabled={submissionStatus === 'submitting'}
                  />
                  {errors.linkedin && (
                    <div className="mt-2">
                      <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg shadow-lg">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium">{errors.linkedin}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label htmlFor="projectType" className="block text-sm font-semibold text-gray-900 mb-2">
                    Project Type *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${
                      errors.projectType 
                        ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-4 focus:ring-red-100' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                    }`}
                    required
                    disabled={submissionStatus === 'submitting'}
                  >
                    <option value="">Select a project type</option>
                    {projectTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.projectType && (
                    <div className="mt-2">
                      <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg shadow-lg">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium">{errors.projectType}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="budget" className="block text-sm font-semibold text-gray-900 mb-2">
                    Budget Range
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${
                      errors.budget 
                        ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-4 focus:ring-red-100' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                    }`}
                    disabled={submissionStatus === 'submitting'}
                  >
                    <option value="">Select budget range</option>
                    {budgetRanges.map((range, index) => (
                      <option key={index} value={range}>{range}</option>
                    ))}
                  </select>
                  {errors.budget && (
                    <div className="mt-2">
                      <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg shadow-lg">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium">{errors.budget}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-sm font-semibold text-gray-900 mb-2">
                    Timeline
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${
                      errors.timeline 
                        ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-4 focus:ring-red-100' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                    }`}
                    disabled={submissionStatus === 'submitting'}
                  >
                    <option value="">Select timeline</option>
                    {timelines.map((time, index) => (
                      <option key={index} value={time}>{time}</option>
                    ))}
                  </select>
                  {errors.timeline && (
                    <div className="mt-2">
                      <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg shadow-lg">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium">{errors.timeline}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Description *
                  <span className="text-gray-500 font-normal ml-2">
                    ({formData.message.length}/1000 characters)
                  </span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${
                    errors.message 
                      ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-4 focus:ring-red-100' 
                      : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                  }`}
                  placeholder="Tell us about your project, current challenges, and what you're hoping to achieve with AI..."
                  required
                  disabled={submissionStatus === 'submitting'}
                  maxLength={1000}
                ></textarea>
                {errors.message && (
                  <div className="mt-2">
                    <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg shadow-lg">
                      <div className="flex items-start">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm font-medium">{errors.message}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={submissionStatus === 'submitting'}
                className={`w-full font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform shadow-lg ${
                  submissionStatus === 'submitting'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:scale-105 hover:shadow-xl'
                }`}
              >
                {submissionStatus === 'submitting' ? (
                  'We\'re reading your message...'
                ) : (
                  emailAgentOnline ? '🚀 Send to AI Agent' : 'Send Message'
                )}
              </button>

              {submissionStatus === 'idle' && (
                <p className="text-sm text-gray-500 text-center mt-4">
                  {emailAgentOnline 
                    ? '🤖 AI Email Agent ready - You\'ll get an intelligent response within minutes!' 
                    : 'We\'ll get back to you within 24 hours.'
                  }
                </p>
              )}

              {submissionResult.status === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium">{submissionResult.error}</p>
                  </div>
                  <button
                    onClick={() => setSubmissionResult({ status: 'idle' })}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                  >
                    Try again
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked{' '}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Questions
                </span>
              </h2>
            </div>

            <div className="space-y-8">
              {[
                {
                  question: "How long does a typical AI project take?",
                  answer: "Project timelines vary depending on complexity, but most projects range from 6-16 weeks. We provide detailed timelines during our initial consultation."
                },
                {
                  question: "Do you work with existing systems?",
                  answer: "Yes, we specialize in integrating AI solutions with existing business systems and legacy infrastructure to minimize disruption."
                },
                {
                  question: "What's included in your consulting services?",
                  answer: "Our consulting includes strategy development, technology assessment, ROI analysis, implementation planning, and ongoing support recommendations."
                },
                {
                  question: "Do you provide ongoing support?",
                  answer: "Yes, we offer comprehensive support packages including monitoring, optimization, updates, and training for your team."
                },
                {
                  question: "How do you ensure data security?",
                  answer: "We follow enterprise-grade security protocols, including encryption, secure APIs, compliance frameworks, and regular security audits."
                }
              ].map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Schedule a free consultation to discuss your AI project and get expert recommendations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:info@oneaiconsulting.com"
                className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Email Us Directly
                <svg className="w-5 h-5 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </a>
              <a 
                href="https://linkedin.com/company/oneai-consulting"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                Connect on LinkedIn
                <svg className="w-5 h-5 ml-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect x="2" y="9" width="4" height="12"/>
                  <circle cx="4" cy="4" r="2"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
